import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, AlertCircle } from "lucide-react";
import { submitContactForm } from "./Contactservice"; // ← all logic lives here

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormState {
  name: string;
  email: string;
  phone: string;
  message: string;
}

const INITIAL_FORM: FormState = {
  name: "",
  email: "",
  phone: "",
  message: "",
};

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
    if (errorMsg) setErrorMsg(null); // clear error on change
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg(null);

    // Delegate all logic to contactService.js
    const result = await submitContactForm(form);

    setIsSubmitting(false);

    if (result.success) {
      setIsSubmitted(true);
      setForm(INITIAL_FORM);
      // Auto-close after showing success message
      setTimeout(() => {
        setIsSubmitted(false);
        onClose();
      }, 2500);
    } else {
      setErrorMsg(result.error ?? "Something went wrong. Please try again.");
    }
  };

  const handleClose = () => {
    if (isSubmitting) return; // prevent closing mid-submit
    setForm(INITIAL_FORM);
    setErrorMsg(null);
    setIsSubmitted(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm"
          />

          {/* Modal / Bottom Sheet */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-xl max-h-[90vh] flex flex-col bg-[#fcfcfc] dark:bg-[#0a0a0a] border-t md:border border-black/10 dark:border-white/10 rounded-t-[2rem] md:rounded-[2rem] shadow-2xl overflow-hidden"
          >
            {/* Mobile Pull Handle Indicator */}
            <div className="md:hidden flex justify-center pt-3 pb-1 shrink-0">
              <span className="w-10 h-1 rounded-full bg-black/20 dark:bg-white/20" />
            </div>

            {/* Close button */}
            <button
              onClick={handleClose}
              type="button"
              disabled={isSubmitting}
              className="absolute top-4 right-4 md:top-8 md:right-8 z-10 p-2 text-black/50 dark:text-white/50 hover:text-black dark:hover:text-white transition-colors rounded-full hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-30 interactive"
            >
              <X size={20} />
            </button>

            {/* Scrollable Form Body */}
            <div className="overflow-y-auto px-6 pb-7 pt-2 md:p-12 overscroll-contain">
              {/* ── Success State ── */}
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-10 md:py-12 text-center"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mb-5 md:mb-6">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </div>
                  <h3 className="font-Montserrat text-2xl md:text-3xl font-bold text-black dark:text-white mb-2">
                    Request Sent!
                  </h3>
                  <p className="text-black/60 dark:text-white/60 font-sans text-sm md:text-base">
                    We'll get back to you at{" "}
                    <span className="font-semibold text-black dark:text-white">
                      info@kivastech.com
                    </span>{" "}
                    shortly.
                  </p>
                </motion.div>
              ) : (
                <>
                  {/* ── Header ── */}
                  <div className="mb-6 md:mb-10 pr-6 md:pr-0">
                    <h3 className="font-Montserrat text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-black dark:text-white mb-2 md:mb-3">
                      Let's build{" "}
                      <span className="italic font-Montserrat text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-500">
                        together.
                      </span>
                    </h3>
                    <p className="text-black/50 dark:text-white/50 font-sans text-xs md:text-sm leading-relaxed">
                      Leave your details below and our team will reach out to
                      discuss your project.
                    </p>
                  </div>

                  {/* ── Form ── */}
                  <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6" noValidate>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      {/* Name */}
                      <div className="space-y-1.5 md:space-y-2">
                        <label
                          htmlFor="name"
                          className="font-Montserrat text-[10px] uppercase tracking-widest text-black/50 dark:text-white/50"
                        >
                          Name *
                        </label>
                        <input
                          required
                          type="text"
                          id="name"
                          value={form.name}
                          onChange={handleChange}
                          className="w-full bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-xl px-4 py-2.5 md:py-3 text-sm text-black dark:text-white outline-none focus:border-indigo-500 dark:focus:border-indigo-400 transition-colors"
                          placeholder="Enter your Name"
                        />
                      </div>

                      {/* Phone */}
                      <div className="space-y-1.5 md:space-y-2">
                        <label
                          htmlFor="phone"
                          className="font-Montserrat text-[10px] uppercase tracking-widest text-black/50 dark:text-white/50"
                        >
                          Phone
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          value={form.phone}
                          onChange={handleChange}
                          className="w-full bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-xl px-4 py-2.5 md:py-3 text-sm text-black dark:text-white outline-none focus:border-indigo-500 dark:focus:border-indigo-400 transition-colors"
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div className="space-y-1.5 md:space-y-2">
                      <label
                        htmlFor="email"
                        className="font-Montserrat text-[10px] uppercase tracking-widest text-black/50 dark:text-white/50"
                      >
                        Email *
                      </label>
                      <input
                        required
                        type="email"
                        id="email"
                        value={form.email}
                        onChange={handleChange}
                        className="w-full bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-xl px-4 py-2.5 md:py-3 text-sm text-black dark:text-white outline-none focus:border-indigo-500 dark:focus:border-indigo-400 transition-colors"
                        placeholder="Enter your Email"
                      />
                    </div>

                    {/* Message */}
                    <div className="space-y-1.5 md:space-y-2">
                      <label
                        htmlFor="message"
                        className="font-Montserrat text-[10px] uppercase tracking-widest text-black/50 dark:text-white/50"
                      >
                        Project Details (Optional)
                      </label>
                      <textarea
                        id="message"
                        rows={3}
                        value={form.message}
                        onChange={handleChange}
                        className="w-full bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-xl px-4 py-2.5 md:py-3 text-sm text-black dark:text-white outline-none focus:border-indigo-500 dark:focus:border-indigo-400 transition-colors resize-none"
                        placeholder="Tell us about your timeline, budget, or goals..."
                      />
                    </div>

                    {/* Error message */}
                    <AnimatePresence>
                      {errorMsg && (
                        <motion.div
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          className="flex items-center gap-2 text-red-500 text-sm font-sans bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3"
                        >
                          <AlertCircle size={16} className="shrink-0" />
                          <span>{errorMsg}</span>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-black dark:bg-white text-white dark:text-black font-Montserrat text-xs uppercase tracking-widest py-3.5 md:py-4 rounded-xl hover:opacity-90 active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:active:scale-100 flex justify-center items-center h-12 interactive cursor-pointer"
                    >
                      {isSubmitting ? (
                        <div className="w-4 h-4 border-2 border-white/30 dark:border-black/30 border-t-white dark:border-t-black rounded-full animate-spin" />
                      ) : (
                        "Submit Request"
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
